import { useCallback, useEffect, useMemo, useState } from 'react';

const DEFAULT_PAGE_SIZE = 25;
const ALLOWED_SIZES = new Set([10, 25, 50, 100]);

function readStoredSize(key: string | undefined, fallback: number): number {
  if (!key || typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = Number(raw);
    return ALLOWED_SIZES.has(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredSize(key: string | undefined, size: number): void {
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, String(size));
  } catch {
    // localStorage can throw in private mode / quota exceeded — ignore.
  }
}

const EMPTY_DEPS: ReadonlyArray<unknown> = [];

export interface UsePaginationOptions {
  /** Total items across all pages. */
  totalItems: number;
  /** Default page size if no stored preference exists. */
  defaultPageSize?: number;
  /**
   * localStorage key for persisting the user's page-size choice across
   * sessions. Omit to skip persistence (size resets on reload).
   */
  storageKey?: string;
  /**
   * Values that, when changed, should reset the page back to 1 (filters,
   * search query, etc.). Pass primitive deps just like a useEffect array.
   */
  resetDeps?: ReadonlyArray<unknown>;
}

export interface UsePaginationResult<T> {
  page: number;
  pageSize: number;
  totalPages: number;
  setPage: (next: number) => void;
  setPageSize: (next: number) => void;
  /** Slice your already-filtered/sorted array to get the current page rows. */
  paginate: (items: readonly T[]) => T[];
}

/**
 * Pagination state with localStorage-persisted page size and automatic page
 * reset when filter dependencies change. Keeps the page in range when the
 * total item count shrinks (e.g., after a delete).
 */
export function usePagination<T>({
  totalItems,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  storageKey,
  resetDeps = EMPTY_DEPS,
}: UsePaginationOptions): UsePaginationResult<T> {
  const [pageSize, setPageSizeState] = useState<number>(() =>
    readStoredSize(storageKey, defaultPageSize),
  );
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Reset to page 1 whenever filters change.
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDeps);

  // Clamp page if total shrinks below current page (e.g., after delete).
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const setPageSize = useCallback(
    (next: number) => {
      if (!ALLOWED_SIZES.has(next)) return;
      setPageSizeState(next);
      writeStoredSize(storageKey, next);
      setPage(1);
    },
    [storageKey],
  );

  const paginate = useCallback(
    (items: readonly T[]): T[] => {
      const start = (page - 1) * pageSize;
      return items.slice(start, start + pageSize);
    },
    [page, pageSize],
  );

  return useMemo(
    () => ({ page, pageSize, totalPages, setPage, setPageSize, paginate }),
    [page, pageSize, totalPages, setPageSize, paginate],
  );
}
